import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { H2 } from 'components/new-ui/base/typography/styles';
import CreateUpdateNoteForm from 'components/new-ui/composed/note/CreateUpdateNoteForm';
import Note from 'components/new-ui/composed/note/Note';
import { NoteFormFields } from 'components/new-ui/composed/note/types';
import { addNoteToManifest, getNotesForManifest, updateManifestNote, deleteManifestNote } from 'store/manifests/client';
import { useNavigate, useParams } from 'react-router-dom';
import Notification from 'components/ui/base/notification/Notification';
import { ManifestNote } from 'store/manifests/types';
import { ListPayload } from 'core/http/response';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useSession from 'hooks/useSession';
import { HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX } from 'core/utils/constants';

const ManifestNotes = () => {
    const { userInfo } = useSession();
    const navigate = useNavigate();
    const { manifestId } = useParams();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [notes, setNotes] = useState<ListPayload<ManifestNote>>();
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchNotes = useCallback(
        async (id: string) => {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            try {
                const list = await getNotesForManifest(id);
                setNotes(list);
            } catch {
                Notification({
                    description: 'Could not get notes for this manifest',
                    messageTitle: 'Error',
                    type: 'error',
                    placement: 'top',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleSubmit = useCallback(
        async (values: NoteFormFields & { id?: string }): Promise<boolean> => {
            if (!manifestId) {
                Notification({
                    messageTitle: 'Error',
                    description: 'Could not get manifest id',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            }

            setSubmitting(true);
            showGlobalOverlay({ type: 'LoadingOverlay' });
            try {
                if (editingNoteId) {
                    await updateManifestNote(manifestId, editingNoteId, {
                        title: values.title,
                        note: values.content,
                    });

                    Notification({
                        messageTitle: 'Success',
                        description: 'Note updated',
                        type: 'success',
                        placement: 'top',
                    });
                } else {
                    await addNoteToManifest(manifestId, {
                        note: values.content,
                        title: values.title,
                    });

                    Notification({
                        messageTitle: 'Success',
                        description: 'Note created',
                        type: 'success',
                        placement: 'top',
                    });
                }
                setEditingNoteId(null);
                await fetchNotes(manifestId);
                return true;
            } catch (error) {
                Notification({
                    messageTitle: 'Error',
                    description: editingNoteId ? 'Could not update note' : 'Could not create note',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            } finally {
                hideGlobalOverlay();
                setSubmitting(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingNoteId, fetchNotes, manifestId]
    );

    const handleDeleteNote = async (noteId: string) => {
        if (!manifestId) {
            Notification({
                messageTitle: 'Error',
                description: 'Could not get manifest id',
                type: 'error',
                placement: 'top',
            });

            return;
        }

        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await deleteManifestNote(manifestId, noteId);
            Notification({
                messageTitle: 'Success',
                description: 'Note deleted',
                type: 'success',
                placement: 'top',
            });
            await fetchNotes(manifestId);
        } catch (error) {
            Notification({
                messageTitle: 'Error',
                description: 'Could not delete note',
                type: 'error',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleEdit = (noteId: string) => {
        setEditingNoteId(noteId);
    };

    const initialFormValues: NoteFormFields | undefined = useMemo(() => {
        if (!editingNoteId || !notes?.list) return undefined;
        const note = notes.list.find((_note) => _note.id === editingNoteId);
        return note ? { title: note.title ?? '', content: note.note ?? '' } : undefined;
    }, [editingNoteId, notes?.list]);

    useEffect(() => {
        if (!manifestId) {
            navigate('/manifests');
            return;
        }

        fetchNotes(manifestId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manifestId]);

    return (
        <>
            <Row>
                <Col
                    lg={16}
                    style={{
                        // Display an inner vertical scroller for the notes container
                        overflowY: 'auto',
                        height: `calc(100vh - ${HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX}px)`,
                        paddingRight: 24,
                    }}
                >
                    <H2 style={{ marginBottom: 32 }}>Notes</H2>
                    {notes?.list?.map((note) => (
                        <Note
                            note={{
                                content: note.note,
                                title: note.title,
                                id: note.id,
                                createdAt: note.createdAt,
                                createdBy: note.creator.fullName,
                            }}
                            isOwnNote={note.creator.id === userInfo?.individualId}
                            hideBorderBottom
                            onDelete={() => handleDeleteNote(note.id)}
                            onEdit={() => handleEdit(note.id)}
                        />
                    ))}
                </Col>

                <Col lg={8} style={{ paddingLeft: 24 }}>
                    <H2 style={{ marginBottom: 32 }}>{editingNoteId ? 'Edit Note' : 'Add Note'}</H2>
                    <CreateUpdateNoteForm
                        key={editingNoteId ?? 'create'}
                        initialValues={initialFormValues}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                        onCancel={() => setEditingNoteId(null)}
                    />
                </Col>
            </Row>
        </>
    );
};

export default ManifestNotes;
